import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { selectReleases } from 'app/pages/Home/slice/selectors';
import { actions as alertsActions } from 'app/components/AlertsAndNotification/store/slice';
import { AlertsAndNotification } from 'app/components/AlertsAndNotification/store/types';
import { STATUS_VALUES } from 'utils/constants';
import { normalizeStatus } from 'utils/helpers';

/** Stable ruleId for every overdue-feature alert (metadata only; not used for aggregation). */
export const OVERDUE_FEATURE_RULE_ID = 'OVERDUE_FEATURE_TARGET_DATE';

const getFeatureId = (feature: any): string | undefined =>
  feature?.featureId ?? feature?.issueId ?? undefined;

const getFeatureTargetDate = (feature: any): string | undefined =>
  feature?.dates?.targetEnd ?? feature?.dates?.targetStart ?? undefined;

/**
 * A feature is overdue when its target date is in the past AND its status is not terminal
 * (Closed or Completed). Mirrors the generic overdue predicate already used for display purposes
 * on RoadmapCard/Card.tsx:94-96 (normalizeStatus + STATUS_VALUES.COMPLETE/CLOSED) and
 * RoadmapCard/Card.tsx:130-137 (targetEnd falling back to targetStart), applied here to leaf
 * `features` entries.
 *
 * Note (flagged, not silently resolved): FeatureCard/index.tsx:161-199 additionally prioritizes a
 * GA release token over targetEnd/targetStart for feature-type cards when a GA date is set, via a
 * private month/quarter token parser local to that component. That is a display-only nuance for
 * the GA badge on the card itself. It is intentionally NOT replicated here: the ticket AC asks for
 * a generic "feature's target date has passed", and the reference PR (#888,
 * RoadmapCard/hooks/useOverdueFeatureAlerts.ts) used targetEnd/targetStart only — it did not read
 * `ga` at all, despite the ticket brief describing it as "targetEnd/GA date falling back to
 * targetStart". If GA-priority overdue alerts are desired, that would need a follow-up to extract
 * FeatureCard's GA token parsing into a shared utility.
 */
export const isFeatureOverdue = (feature: any): boolean => {
  if (!feature) return false;

  const targetDate = getFeatureTargetDate(feature);
  if (!targetDate) return false;

  const parsedTargetDate = moment(targetDate);
  if (!parsedTargetDate.isValid()) return false;
  if (!parsedTargetDate.isBefore(moment(), 'day')) return false;

  const normalizedStatus = normalizeStatus(feature?.status);
  const isTerminal =
    normalizedStatus === STATUS_VALUES.CLOSED ||
    normalizedStatus === STATUS_VALUES.COMPLETE;

  return !isTerminal;
};

/**
 * Recursively collects every `features` leaf entry reachable from the roadmap release data:
 * releaseScheduled[].projects[].project[] (Card) -> features[] (FeatureCard) and
 * -> childRelationships[] (nested Card), recursed to any depth. Mirrors the tree shape already
 * walked by src/app/utils/applySearchFilterToReleases.ts and
 * MonthlyTimeline/utils/buildMonthlySearchIndex.ts for this same release data.
 */
const collectFeatures = (releaseData: any): any[] => {
  const features: any[] = [];
  const releaseScheduled: any[] = releaseData?.releaseScheduled || [];

  const visitCard = (card: any): void => {
    if (!card) return;
    (card.features || []).forEach((feature: any) => features.push(feature));
    (card.childRelationships || []).forEach(visitCard);
  };

  releaseScheduled.forEach((releaseType: any) => {
    (releaseType?.projects || []).forEach((quarterProject: any) => {
      (quarterProject?.project || []).forEach(visitCard);
    });
  });

  return features;
};

/** Returns the de-duplicated list of overdue features found in the roadmap release data. */
export const getOverdueFeatures = (releaseData: any): any[] => {
  const seen = new Set<string>();
  const overdue: any[] = [];

  collectFeatures(releaseData).forEach(feature => {
    if (!isFeatureOverdue(feature)) return;

    const id = getFeatureId(feature);
    if (!id || seen.has(id)) return;
    seen.add(id);
    overdue.push(feature);
  });

  return overdue;
};

/**
 * Builds one navigable ALERT per overdue feature — never aggregated — so every overdue feature
 * keeps its own `issueId` for navigation. AlertsContainer.handleAlertAction routes via
 * `alert.issueId` -> `openSlideout(issueId)` (see
 * AlertsAndNotification/components/AlertsContainer/AlertsContainer.tsx).
 */
export const buildOverdueAlert = (feature: any): AlertsAndNotification => {
  const issueId = getFeatureId(feature) as string;
  const title = feature?.title || 'Feature';

  return {
    id: `overdue-feature-${issueId}`,
    userId: '',
    ruleId: OVERDUE_FEATURE_RULE_ID,
    category: 'ALERT',
    severity: 'WARNING',
    actionable: true,
    ctaLabel: 'View feature',
    status: 'UNREAD',
    sticky: false,
    issueId,
    momentName: title,
    groupKey: `overdue-feature-${issueId}`,
    dedupKey: `overdue-feature-${issueId}`,
    uiCopy: `"${title}" target date has passed`,
    headline: 'Overdue feature',
    link: issueId,
    dismissedIssueKeys: [],
    createdAt: ''
  };
};

/**
 * Computes overdue-feature alerts (target date in the past AND status not Closed/Completed) from
 * the roadmap release data and publishes them into the AlertsAndNotification slice under the
 * dedicated `overdueAlerts` key — one alert per overdue feature, never aggregated, so each keeps
 * its own `issueId` for navigation (AC: "Users can navigate from the alert to the corresponding
 * feature"). Re-derives whenever the release data changes, so alerts disappear automatically once
 * a feature is marked Closed/Completed or its target date moves to the future (AC: "Alerts are
 * removed automatically once the feature is marked as Closed"). Clears on unmount so stale alerts
 * don't persist across route changes.
 */
export const useOverdueFeatureAlerts = (): AlertsAndNotification[] => {
  const dispatch = useDispatch();
  const releases = useSelector(selectReleases);

  const overdueAlerts = useMemo<AlertsAndNotification[]>(
    () => getOverdueFeatures(releases).map(buildOverdueAlert),
    [releases]
  );

  useEffect(() => {
    dispatch(alertsActions.setOverdueAlerts(overdueAlerts));
  }, [dispatch, overdueAlerts]);

  useEffect(() => {
    return () => {
      dispatch(alertsActions.setOverdueAlerts([]));
    };
  }, [dispatch]);

  return overdueAlerts;
};

export default useOverdueFeatureAlerts;
